import React, { useEffect, useState } from 'react';
import { IOrderDetails, OrderDetailsResponse } from './types';
import ReactDOM from 'react-dom';
import { InitialUrlSearchParams } from '../../../../utils/environment';
import { LoadingMoreSpinner } from '../../../product-listing-page/components/SmallUIComponents';
import OrderDetailsHeader from './OrderDetailsHeader';
import ShipmentCard from './ShipmentCard';
import ErrorUI from './ErrorUI';
import { aloApiAuthFetch } from '../../../../utils/alo-api-fetch';
import { prependLocaleToPath } from '../../../../utils/localization';
import { IS_NATIVE_APP_CLIENT } from '../../../../utils/helpers';
import { getOrderCountryCode } from './helpers';
import { generateToken } from '../../../../utils/token-generator';

const urlParams = InitialUrlSearchParams;
const orderReferenceFromURLparams = urlParams.get('order_reference') || '';
const tokenFromURLparams = urlParams.get('token');
const emailFromURLparams = urlParams.get('email');

const OrderDetails = () => {
  const [order, setOrder] = useState<IOrderDetails | null>(null);
  const [isLoading, setLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [token, setToken] = useState<string | null>(null);

  useEffect(() => {
    const fetchOrders = async () => {
      setLoading(true);
      // Use token if available, otherwise generate token from email
      let authParam = '';
      if (tokenFromURLparams) {
        authParam = `token=${tokenFromURLparams}`;
        setToken(tokenFromURLparams);
      } else if (emailFromURLparams && orderReferenceFromURLparams) {
        try {
          const generatedToken = await generateToken(orderReferenceFromURLparams, emailFromURLparams);
          authParam = `token=${generatedToken}`;
          setToken(generatedToken);
        } catch (error) {
          console.error('Failed to generate token:', error);
        }
      }

      aloApiAuthFetch(`/v1/orders/${orderReferenceFromURLparams}${authParam ? `?${authParam}` : ''}`)
        .then((res) => res.json())
        .then((response: OrderDetailsResponse) => {
          const error = response.detail;
          if (error) {
            setIsError(true);
            console.log('Alo API error: ', error);
          } else {
            response.order ? setOrder(response.order) : setIsError(true);
          }
          setLoading(false);
        });
    };
    fetchOrders();
  }, []);

  if (isError) return <ErrorUI isOrderDetails={true} />;
  if (isLoading)
    return (
      <div className="container order-details-page">
        <div className="row">
          <div style={{ display: 'grid', placeContent: 'center', width: '100vw', height: '100vh' }} className="col-12">
            <div>
              <LoadingMoreSpinner />
              <p className="editorial">Loading Order Details</p>
            </div>
          </div>
        </div>
      </div>
    );
  if (!order) return null;
  const orderCountryCode = getOrderCountryCode(order);
  const segments = order.segments;
  const sortedSegments = [...segments].sort((a, b) => a.sequence - b.sequence);

  return (
    <div className="container order-details-page pb-lg-5">
      <div className="row">
        <div className="col-12">
          <section className="odp-breadcrumbs">
            {!IS_NATIVE_APP_CLIENT && order.authenticated && (
              <p className="body">
                <a href={prependLocaleToPath('/account')} className="body underline">
                  Order History
                </a>{' '}
                / Order #{order.order_number}
              </p>
            )}
          </section>
          <OrderDetailsHeader order={order} />
          <section className="order-shipments">
            {sortedSegments.map((segment) => (
              <ShipmentCard
                key={`${segment.type}-${segment.sequence}`}
                segment={segment}
                orderReference={orderReferenceFromURLparams}
                token={token}
                totalSegments={sortedSegments.length}
                orderShippingCountryCode={orderCountryCode}
              />
            ))}
          </section>
        </div>
      </div>
    </div>
  );
};

export default OrderDetails;

const renderOrderDetails = () => {
  const mountNode = document.querySelector('[react-render-target="order-details"]');
  if (mountNode) ReactDOM.render(<OrderDetails />, mountNode);
  else console.warn('missing render target: order-details');
};

export const InitOrderDetails = () => {
  renderOrderDetails();
};
